import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@apollo/client';

import {
  CoreMyListingsSellerListingCards,
  reverb_search_SearchResponse,
} from '@reverbdotcom/commons/src/gql/graphql';

import LoadingOverlay from '../../shared/loading_overlay';
import PlaceholderSellerListingCard from './PlaceholderSellerListingCard';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';
import SellerListingCardContainer from './SellerListingCardContainer';
import SellerListingCardsToolbar from './SellerListingCardsToolbar';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import sellerListingsCollectionReducer, { ActionType } from './sellerListingsCollectionReducer';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useLazySellerListingsQuery } from './useLazySellerListingsQuery';
import EMPTY_STATE_IMAGE from '../../../images/icons/empty-states/no-listings@2x.png';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

export const SHIPPING_REGION_CODES = [
  EVERYWHERE_CODE,
];

export const COMPONENT_NAME = 'SellerListingCardsCollection';

interface IExternalProps {
  listingIds: string[] | number[];
}

export function SellerListingCardsCollection({ listingIds }: IExternalProps) {
  const { data = {}, loading } = useQuery<CoreMyListingsSellerListingCards.Query, CoreMyListingsSellerListingCards.Variables>(SELLER_LISTINGS_QUERY, {
    ssr: false,
    variables: {
      listingIds: listingIds.map(id => id.toString()),
      shippingRegionCodes: SHIPPING_REGION_CODES,
    },
    errorPolicy: 'all',
  },
  );

  const { me = {} } = data;
  const { shop = {} } = me;
  const { listings: listingsResponse } = shop;
  const {
    listings = [],
  }: reverb_search_SearchResponse = listingsResponse ?? {};
  const placeholderListings = Array.from(Array(listingIds.length).keys());
  const shouldRenderPlaceholderListings = loading;

  const sanitizedAndSortedListings = listingIds.map(id => listings.find(x => x.id === id))
    .filter(listing => listing !== undefined)
    .map(listing => {
      return { ...listing, ...{ loading: false } };
    });

  const initialListingsCollectionState = {
    selectedListings: [],
    listings: [],
    showEmptyState: false,
  };

  const [listingsCollectionState, dispatch] = React.useReducer(sellerListingsCollectionReducer, initialListingsCollectionState);

  const { listings: sellerListings, showEmptyState } = listingsCollectionState;

  React.useEffect(() => {
    if (loading) return;

    dispatch({
      type: ActionType.ADD_INITIAL_LISTINGS,
      payload: {
        listings: sanitizedAndSortedListings,
      },
    });
  }, [loading]);

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, !loading);

  return (
    <LoadingOverlay showOverlay={loading}>
      <SellerListingsCollectionContext.Provider
        value={{
          listingsCollectionState,
          dispatch,
          useLazySellerListingsQuery,
        }}
      >
        <div className="seller-listing-cards-collection__listings">
          <SellerListingCardsToolbar />

          {shouldRenderPlaceholderListings ? placeholderListings.map(element => (
            <div key={`placeholderListing-${element}`}>
              <PlaceholderSellerListingCard />
            </div>
          )) : sellerListings.map(listing => (
            <div key={listing.id}>
              <SellerListingCardContainer
                listing={listing}
              />
            </div>
          ))}
        </div>

        {showEmptyState && (
          <div className="d-flex fx-dir-col gap-3 fx-align-center ptb-6">
            <img
              className="mb-4"
              src={EMPTY_STATE_IMAGE}
              alt={I18n.t('discovery.listingsManagement.emptyState.noListings')}
            />

            <div className="weight-bold size-120">
              {I18n.t('discovery.listingsManagement.emptyState.noListings')}
            </div>
          </div>
        )}
      </SellerListingsCollectionContext.Provider>
    </LoadingOverlay>
  );
}

export const SellerListingCardFragment = gql`
  fragment SellerListingCard on Listing {
    _id
    id
    sellerUuid
    title
    slug
    sku
    state
    inventory
    condition {
      displayName
    }
    bumpRate {
      rate
    }
    sellerPrice {
      amount
      amountCents
      symbol
      currency
      display
    }
    images(input: { transform: "card_square", count: 1 }) {
      source
    }
    counts {
      views
      offers
      watchers
    }
    orderStats {
      totalOrders
    }
    publishedAt {
      seconds
    }
    hasInventory
    isAutoOfferEligible
    affirmZeroPercentFinancingConfiguration {
      isEligible
      isEnabled
    }
    autoOffers {
      id
    }
    shop {
      _id
      currency
      canCurateSales
    }
    exclusiveChannel
    relistable
    publishValidation {
      isValid
      errors {
        message
      }
    }
  }
`;

export const SELLER_LISTINGS_QUERY = gql`
query Core_MyListings_SellerListingCards($listingIds: [String], $shippingRegionCodes: [String]) {
  me {
    uuid
    shop {
      _id
      listings(input: { ids: $listingIds, shippingRegionCodes: $shippingRegionCodes }) {
        listings {
          _id
          ...SellerListingCard
        }
      }
    }
  }
},
${SellerListingCardFragment}
`;

export default SellerListingCardsCollection;
